import { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, Download, AlertCircle, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { ApiResponse } from '@/types';

interface ImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: 'activities' | 'campaigns' | 'suppliers';
  planId?: string;
  onSuccess: () => void;
}

interface ValidationResult {
  headers: string[];
  column_mapping: Record<number, string>;
  total: number;
  valid_count: number;
  invalid_count: number;
  valid_rows: Record<string, unknown>[];
  invalid_rows: { row: number; data: Record<string, unknown>; errors: string[] }[];
  preview: Record<string, unknown>[];
}

type Step = 'upload' | 'preview' | 'result';

const ENTITY_LABELS: Record<string, string> = {
  activities: 'Aktivitäten',
  campaigns: 'Kampagnen',
  suppliers: 'Zulieferer',
};

export function ImportDialog({ open, onOpenChange, entityType, planId, onSuccess }: ImportDialogProps) {
  const [step, setStep] = useState<Step>('upload');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [importResult, setImportResult] = useState<{ imported: number; failed: number; errors: { row: number; error: string }[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep('upload');
    setLoading(false);
    setError('');
    setValidation(null);
    setImportResult(null);
  };

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file) return;
    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.upload<ApiResponse<ValidationResult>>(`/import/validate/${entityType}`, formData);
      if (!res.success) { setError(res.error || 'Validierung fehlgeschlagen'); return; }
      setValidation(res.data!);
      setStep('preview');
    } catch (err: any) {
      setError(err.message || 'Fehler beim Hochladen');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!validation) return;
    setLoading(true);
    setError('');

    try {
      const res = await api.post<ApiResponse<{ imported: number; failed: number; errors: { row: number; error: string }[] }>>(`/import/execute/${entityType}`, {
        rows: validation.valid_rows,
        plan_id: planId,
      });
      if (!res.success) { setError(res.error || 'Import fehlgeschlagen'); return; }
      setImportResult(res.data!);
      setStep('result');
      if (res.data!.imported > 0) onSuccess();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Import');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    window.open(`/api/import/template/${entityType}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {ENTITY_LABELS[entityType]} importieren
            {step !== 'upload' && (
              <span className="ml-2 text-sm font-normal text-muted">
                Schritt {step === 'preview' ? '2/3' : '3/3'}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {step === 'upload' && (
          <div className="space-y-4">
            <div
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-background p-8 transition-colors hover:border-border hover:bg-card-hover dark:bg-card-hover dark:hover:bg-primary-hover/10"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFileSelect(e.dataTransfer.files); }}
            >
              <Upload size={32} className="mb-3 text-muted" />
              <p className="text-sm font-medium text-muted">CSV-Datei hierher ziehen oder klicken</p>
              <p className="mt-1 text-xs text-muted">Unterstützt: .csv (Komma- oder Semikolon-getrennt)</p>
              <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => handleFileSelect(e.target.files)} />
            </div>

            <button onClick={downloadTemplate} className="flex items-center gap-2 text-sm text-foreground dark:text-muted">
              <Download size={14} /> CSV-Vorlage herunterladen
            </button>
          </div>
        )}

        {step === 'preview' && validation && (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex items-center gap-2 rounded-lg bg-success-soft px-3 py-2 text-sm">
                <CheckCircle2 size={16} className="text-success" />
                <span className="font-medium text-success">{validation.valid_count} gültig</span>
              </div>
              {validation.invalid_count > 0 && (
                <div className="flex items-center gap-2 rounded-lg bg-danger-soft px-3 py-2 text-sm">
                  <AlertCircle size={16} className="text-danger" />
                  <span className="font-medium text-danger">{validation.invalid_count} fehlerhaft</span>
                </div>
              )}
            </div>

            <div className="max-h-64 overflow-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-background dark:border-border dark:bg-card-hover">
                    {Object.values(validation.column_mapping).map((col) => (
                      <th key={col} className="px-3 py-2 text-left text-xs font-medium text-muted">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {validation.preview.map((row, i) => (
                    <tr key={i} className="border-b border-border">
                      {Object.values(validation.column_mapping).map((col) => (
                        <td key={col} className="px-3 py-1.5 text-muted">{String(row[col] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {validation.invalid_rows.length > 0 && (
              <div className="max-h-32 overflow-auto rounded-lg border border-danger/30 p-3">
                <p className="mb-1 text-xs font-medium text-danger">Fehlerhafte Zeilen:</p>
                {validation.invalid_rows.slice(0, 5).map((inv) => (
                  <p key={inv.row} className="text-xs text-danger">Zeile {inv.row}: {inv.errors.join(', ')}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 'result' && importResult && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 py-4">
              <CheckCircle2 size={48} className="text-success" />
              <p className="text-lg font-semibold text-foreground">
                {importResult.imported} von {importResult.imported + importResult.failed} importiert
              </p>
            </div>
            {importResult.errors.length > 0 && (
              <div className="max-h-32 overflow-auto rounded-lg border border-danger/30 p-3">
                {importResult.errors.map((e) => (
                  <p key={e.row} className="text-xs text-danger">Zeile {e.row}: {e.error}</p>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && loading && <p className="text-sm text-muted">Wird validiert...</p>}
          {step === 'preview' && (
            <>
              <Button type="button" variant="outline" onClick={reset}>
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </Button>
              <Button
                type="button"
                onClick={handleImport}
                disabled={loading || !validation || validation.valid_count === 0}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Importiere…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {validation?.valid_count} Einträge importieren
                  </>
                )}
              </Button>
            </>
          )}
          {step === 'result' && (
            <Button type="button" onClick={() => onOpenChange(false)}>
              <CheckCircle2 className="h-4 w-4" />
              Schließen
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
